import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { AdminPagesService } from '../admin/adminPagesSlice'
import PageHeadSection from '../admin/components/PageHeadSection'
import SectionBoxContainer from '../../components/SectionBoxContainer'
import NotFound from '../../components/NotFound'
import GenerateForm from '../../components/Form/GenerateForm'
import FormActionButtonsSection from '../../components/Form/FormActionButtonsSection'
import { LoadingButton } from '@mui/lab'
import DynamicForm, { IDynamicForm, IDynamicFormElement, IRequiredField } from '../../components/Form/DynamicForm'
import { addNewForm, createOrUpdateElementData } from '../../components/Form/formsSlice'
import IssuesApp, { EmptyIssueModel, IIssuesModal } from './IssuesApp'
import { handleRequestError } from '../../services/api'
import { IAxiosDefaultDataObject } from '../../config'
import { updateIssue } from './issuesSlice'
import { addNewUser, useReturnAllUsers, useReturnCurrentUser } from '../user/userSlice'
import UsersApp from '../user/UsersApp'

interface IProps {
    noRedirect?: boolean,
    customId?: number,
    afterSuccess?: () => void,
    advancedAfterSuccess?: (item: IAxiosDefaultDataObject) => void,
    hidePageHeadSection?: boolean
}

export default function ResolveIssue(props: IProps) {

    const { noRedirect, customId, afterSuccess, hidePageHeadSection, advancedAfterSuccess } = props

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const params = useParams()
    const [notFound, setNotFound] = useState<boolean>(false)

    const [issueDetails, setIssueDetails] = useState<IIssuesModal>(EmptyIssueModel)
    const [itemId, setItemId] = useState<number>(customId ? customId : params.id ? Number(params.id) : 0)
    const [isUpdate, setIsUpdate] = useState<boolean>(true) 

    const page = AdminPagesService.usePageDetails("issues")
    const issuesApp = new IssuesApp()

    const elemPrefix: string = issuesApp.elemPrefix

    const currentUser = useReturnCurrentUser()

    const defaultElems: IDynamicFormElement[] = [
                {
                    id: issuesApp.elemPrefix + "resolved_text",
                    xs: 12,
                    type: "TextField",
                    label: "Description",
                    fullWidth: true,
                    multiline: true,
                    minRows: 4,
                },
                {
                    id: issuesApp.elemPrefix + "resolved_images",
                    xs: 12,
                    type: "Images",
                    label: "Images",
                    fullWidth: true
                },
    ]

    const form: IDynamicForm = {
        name: "resolve-issue-form",
        elements: currentUser.is_superuser ? [
            // {
            //     id: elemPrefix + "resolved_description",
            //     xs: 12,
            //     type: "BlockEditor",
            //     label: "Description",
            //     fullWidth: true,
            // },
            {
                id: elemPrefix + "resolved_by",
                xs: 6,
                type: "DynamicModelSelect",
                label: "Resolved by",
                fullWidth: true,
                appClass: new UsersApp(),
                selectLabel: "Select User",
                gridColumns: new UsersApp().gridDataColumns
            },
            {
                id: elemPrefix + "resolved_at",
                xs: 6,
                type: "DateAndTime",
                label: "Resolved on",
                fullWidth: true,
                // defaultValue: new Date()
            },
            ...defaultElems
        ] : [
            // {
            //     id: elemPrefix + "resolved_description",
            //     xs: 12,
            //     type: "BlockEditor",
            //     label: "Description",
            //     fullWidth: true,
            // },
            {
                id: elemPrefix + "resolved_at",
                xs: 12,
                type: "DateAndTime",
                label: "Resolved on",
                fullWidth: true,
                // defaultValue: new Date()
            },
            ...defaultElems
        ]
    }

    const currentForm = DynamicForm.useReturnSingleForm(form.name)

    const elementsData = DynamicForm.useReturnAllFormsData()
    const singleElemDataValue = (name: string) => DynamicForm.returnSingleFormElementData(name, elementsData).value

    /**
     * Create form elem data
     */
    const createFormElemData = () => {
        form.elements.map((elem) => dispatch(createOrUpdateElementData({
            name: elem.id,
            value: ""
        })))
    }

    /**
     * Reset form
     */
    const resetForm = () => {
        createFormElemData()
        DynamicForm.createOrUpdateFormMessage(form.name, { shown: false, type: "success", message: "" }, dispatch)
    }
    
    /**
     * Load item
     */
    const loadItem = () => {
        DynamicForm.updateFormLoadingStatus(form.name, true, dispatch)
        issuesApp.singleInstanceDetails(itemId ? itemId : 0)
            .then(response => {

                
                const item = response.data

                form.elements.map((elem) => dispatch(createOrUpdateElementData({
                    name: elem.id,
                    value: item[elem.id.replace(elemPrefix, "")]
                })))

                setIssueDetails(item)

                setNotFound(false)
                DynamicForm.updateFormLoadingStatus(form.name, false, dispatch)

            })
            .catch(error => {

                handleRequestError(error, dispatch)

                switch(error.response.status){

                    case 404:
                        setNotFound(true)

                }

                DynamicForm.updateFormLoadingStatus(form.name, false, dispatch)
            })
    }
 

    /**
     * Handle submit
     */
    const handleSubmit = () => {
        let data: IAxiosDefaultDataObject = {}

        /**
         * Fill data
         */
        form.elements.map((elem) => {
            let dataName = elem.id.replace(elemPrefix, "")
            data[dataName] = singleElemDataValue(elem.id)
        })

        let requiredFields: Array<IRequiredField> = [
            {
                id: elemPrefix + "resolved_by",
                message: "Resolved by is required!"
            },
            {
                id: elemPrefix + "resolved_at",
                message: "Resolved on by is required!"
            },
        ]

        if (isUpdate){
            data.id = itemId
            data.update = true
        }

        DynamicForm.resetRequiredFieldsHelperText(requiredFields, elementsData, dispatch)
        const verifyForm = DynamicForm.verifyForm(elementsData, requiredFields)

        if (verifyForm.isValid){
            DynamicForm.updateFormLoadingStatus(form.name, true, dispatch)

            issuesApp.resolveIssue(itemId, data)
                .then(response => {

                    const { data } = response
                    const message = data.message ? data.message : "Success!"

                    DynamicForm.updateFormLoadingStatus(form.name, false, dispatch)
                    DynamicForm.createOrUpdateFormMessage(form.name, { shown: true, type: "success", message: message }, dispatch)

                    dispatch(updateIssue(data))
                    
                    if (advancedAfterSuccess){
                        advancedAfterSuccess(data)
                    }

                    resetForm()

                    if (!noRedirect){
                        navigate(`/panel/${page.name}`)
                    }

                    if (afterSuccess){
                        afterSuccess()
                    }

                })
                .catch(error => {

                    console.log(error)
                    const message = error.response.data.message ? error.response.data.message : "Fatal Error"

                    DynamicForm.updateFormLoadingStatus(form.name, false, dispatch)
                    DynamicForm.createOrUpdateFormMessage(form.name, { shown: true, type: "error", message: message }, dispatch)

                })
        }else{
            DynamicForm.updateRequiredFieldsHelperText(verifyForm.fields, elementsData, dispatch)
        }
    }

    /**
     * Create Form
     */
    useEffect(() => {

        dispatch(addNewForm({
            name: form.name,
            elements: form.elements
        }))

        createFormElemData()

    }, [])

    /**
     * Load issue
     */
    useEffect(() => {

        if (itemId){
            setIsUpdate(true)
        }

        if (isUpdate && itemId){
            loadItem()
        }else{
            setIsUpdate(false)
            resetForm()
        }

    }, [itemId])

    return(
        <div>
            {!hidePageHeadSection && <PageHeadSection customPageType="edit" pageName={page.name} customTitle="Resolve Issue" />}

            <SectionBoxContainer
                showTitle={true}
                title="Resolve Issue"
                description={issueDetails.issue_type == "missing" ? "Missing quantity will be added automatically back to the item." : ""}
            >
                {notFound ? <NotFound /> : issueDetails.resolved ? <NotFound  title="Resolved" message="Issue has already been resolved." />  : <GenerateForm name={form.name} />}

            </SectionBoxContainer>

            {!issueDetails.resolved && (
                <FormActionButtonsSection>
                    <LoadingButton disabled={notFound} loading={currentForm.loading} className="general-button submit-style" variant="contained" onClick={() => handleSubmit()} >Resolve Issue</LoadingButton>
                </FormActionButtonsSection>
            )}
        </div>
    )
}